import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  StatBand,
  Timeline,
  Prose,
  Lead,
  CTASection,
  Reveal,
} from './kit.jsx'

const STATS = [
  { value: '10,942', label: 'Verified B2B leads' },
  { value: '13', label: 'Regions loaded' },
  { value: '1', label: 'Workspace, end to end' },
  { value: '50', label: 'U.S. regions covered' },
]

const VALUES = [
  {
    icon: 'shield',
    title: 'Real data, not scraped guesses',
    body: 'Every company is verified, cleaned, and organized by industry and region. No mystery lists, no dead numbers, no "verified" leads that bounce on the first send.',
  },
  {
    icon: 'hammer',
    title: 'Reps sell, we source',
    body: 'Finding the accounts is our job. We ship the entire addressable market pre-loaded so your team spends its hours quoting and closing, not hunting for names in spreadsheets.',
  },
  {
    icon: 'mail',
    title: 'Own your outreach',
    body: 'Sequences send from your own Gmail as yourself, and replies land in your real inbox. You own the relationship and the deliverability — we never blast on your behalf from a shared domain.',
  },
  {
    icon: 'rocket',
    title: 'Ship weekly',
    body: 'We are operators at heart: small team, fast cadence. New regions, filters, and sequence tooling go out on a weekly rhythm, driven by what customers actually ask for.',
  },
  {
    icon: 'lock',
    title: 'Your data stays yours',
    body: 'Cognito auth, per-account isolation, and no reselling of your pipeline. What your reps log, who they contact, and what they win belongs to you and no one else.',
  },
  {
    icon: 'target',
    title: 'One workspace, no patchwork',
    body: 'Database, outreach, pipeline, and analytics live together. We would rather build the boring integration once than watch sales teams juggle six tabs to run one sales motion.',
  },
]

const MILESTONES = [
  {
    date: 'Jan 2026',
    tag: 'Founded',
    title: 'Revailo Sales starts with one frustration',
    body: 'After watching sales teams burn thousands on junk lead lists and then re-key them into spreadsheets, we set out to ship the whole market pre-loaded — sourcing, outreach, and pipeline in a single workspace.',
  },
  {
    date: 'Mar 2026',
    title: 'Verified data, cleaned and loaded',
    body: 'We finished normalizing and de-duplicating the full company export into per-category tables organized by industry and region.',
    bullets: [
      '10,942 companies verified and de-duplicated',
      '50 U.S. regions scoped so reps only see their territory',
      'Contacts, service area, established year, and annual volume on every record',
    ],
  },
  {
    date: 'Apr 2026',
    tag: 'Shipped',
    title: 'Gmail outreach goes live',
    body: 'Multi-step email sequences with waits and follow-ups launched, sending from each user\'s own Gmail so replies come back to their inbox and log automatically against the account.',
  },
  {
    date: 'May 2026',
    title: 'Deal Flow and analytics',
    body: 'The people-level pipeline shipped alongside Activity and Data views, rolling revenue and reply signals up by region, lead quality, and campaign.',
  },
  {
    date: 'Jun 2026',
    tag: 'New',
    title: 'In-app assistant and team collaboration',
    body: 'Ask questions in plain English against your live workspace, invite reps, assign regions, and work a single source of truth — with dark and light themes across the whole app.',
  },
]

export default function AboutPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="About"
        description="Revailo Sales exists so B2B sales teams stop buying junk lead lists and juggling spreadsheets. 10,942 verified companies, outreach, and pipeline in one workspace."
        path="/about"
        keywords="about Revailo Sales, B2B lead engine, B2B lead data company, verified company data"
      />

      <PageHero
        eyebrow="About"
        title={
          <>
            Built by people who got tired <br className="hidden sm:block" />
            of buying bad lead lists.
          </>
        }
        lead="Revailo Sales is the lead engine we wished existed when we were cold-calling accounts off a stale CSV — the whole market pre-loaded, outreach from your own inbox, and a pipeline that tells you where the revenue is."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'See open roles', to: '/careers' }}
      />

      <Section id="story">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Why we exist"
              title="Sales teams deserve better than a spreadsheet and a hunch"
              lead="Sourcing is the least glamorous part of a contracting business — and it is where teams waste the most time."
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 720, margin: '40px auto 0' }}>
            <Reveal>
              <Prose>
                <p>
                  Every B2B business runs on the same broken loop: buy a
                  lead list, discover half the contacts are dead, paste the survivors into a
                  spreadsheet, and then copy-and-paste cold emails one at a time until someone gives
                  up. The data goes stale, the follow-ups slip, and nobody can say which regions or
                  campaigns are actually producing jobs.
                </p>
                <p>
                  We built Revailo Sales to delete that loop entirely. The whole addressable market —
                  10,942 verified companies — is already
                  loaded, organized by industry and region, with real contacts and company detail on
                  every record. On top of it sits the rest of the sales motion: multi-step outreach
                  sequences that send from your own Gmail, a deal-flow pipeline, activity and
                  analytics, and an in-app assistant that answers questions straight from your live
                  data.
                </p>
              </Prose>
            </Reveal>
            <Reveal>
              <Lead className="mt-6">
                No scraping project. No six-month rollout. Sign in and your market is already there —
                so your reps can spend their day selling, not sourcing.
              </Lead>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section id="principles">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="What we believe"
              title="Principles we build against"
              lead="Every feature decision runs through these. When we are not sure what to ship next, these settle it."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={VALUES} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section id="milestones">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="How we got here"
              title="From one frustration to a full workspace"
              lead="A short company built in public, shipping the pieces customers kept asking for."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <Timeline items={MILESTONES} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Join us"
        title="Come help sales teams win more deals."
        body="Start free and see every qualified company in your market today — or come build the lead engine itself. We are hiring across data, product, and sales."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'View careers', to: '/careers' }}
      />
    </MarketingShell>
  )
}
